import { calcBaseTaxPrices, hasValidPositiveVehicleWeight } from './baseTax';
import { isElectric, normalizeVehicle } from './ev';

const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const MIDNIGHT = { hour: 0, minute: 0, second: 0 };

// Whole-month difference used for late-fee ageing and inspection alerts.
export function findAge(moment, dateOne, dateTwo) {
  const diffYear = moment(dateOne).year() - moment(dateTwo).year();
  let diffMonth =
    moment(dateOne).month() + 1 - (moment(dateTwo).month() + 1) + diffYear * 12;
  const diffDay = moment(dateOne).date() - moment(dateTwo).date();
  if (diffDay > 0) diffMonth++;
  return diffMonth;
}

function roundMoney(value, scrapMode) {
  // Sums of several percentage-of-integer terms (e.g. tax * 0.01 * 36) rarely
  // land on an exact float, so a true whole number can arrive as something
  // like 1524.0000000000002. Snap that noise away before rounding up to the
  // cent, otherwise Math.ceil bumps it to the next cent (1524.01) instead.
  const cleaned = Math.round(value * 1e8) / 1e8;
  let rounded = Math.ceil(cleaned * 100) / 100;
  if (rounded === 0.01) rounded = 0;
  if (scrapMode == 1) rounded = Math.floor(rounded);
  else if (scrapMode == 2) rounded = Math.ceil(rounded);
  return rounded;
}

// รย.1 non-electric: the tax steps down with the age of the car (in months).
function taxByYearForAge(carAge, p) {
  if (carAge > 132) return [p[5], p[5], p[5], p[5]];
  if (carAge > 120) return [p[4], p[5], p[5], p[5]];
  if (carAge > 108) return [p[3], p[4], p[5], p[5]];
  if (carAge > 96) return [p[2], p[3], p[4], p[5]];
  if (carAge > 84) return [p[1], p[2], p[3], p[4]];
  if (carAge > 72) return [p[0], p[1], p[2], p[3]];
  if (carAge > 60) return [p[0], p[0], p[1], p[2]];
  if (carAge > 48) return [p[0], p[0], p[0], p[1]];
  return [p[0], p[0], p[0], p[0]];
}

function inspectionMessage(input, carType, moment, now) {
  if (
    input.dialogYearChoose ||
    !moment(input.registrationDate).isValid() ||
    !moment(input.taxoutDate).isValid()
  ) {
    return '';
  }
  const ageInspect = findAge(moment, now, input.registrationDate);
  const ageInspectTax = findAge(moment, now, input.taxoutDate);
  const isCar = carType === 'CT1' || carType === 'CT2' || carType === 'CT3';

  if (ageInspectTax > 12 && (isCar || carType === 'CT12')) {
    return 'รถขาดเกินปี ต้องตรวจสภาพด้วย';
  }
  if (ageInspect < 81 && isCar) return 'รถยังไม่ถึงเกณฑ์ที่ต้องตรวจสภาพ';
  if (ageInspect < 57 && carType === 'CT12') return 'รถยังไม่ถึงเกณฑ์ที่ต้องตรวจสภาพ';
  return 'รถถึงเกณฑ์ที่ต้องตรวจสภาพ';
}

// Tax for one vehicle: base rate, late fees, optional next-year prepay, gas/EV
// and corporate adjustments. Pure: every input is a plain value, no component
// state. `deps.moment` is injected so this file works in both Vue 2 and Vue 3.
//
// input: {
//   carType, carWeight, engineSize, carFuel, extraFuel, isCorporationCar,
//   registrationDate, taxoutDate, billTaxoutDate, taxFillingDate,
//   isActiveTaxYearEnd, isActiveTaxYearInit, dialogYearChoose,
//   carTaxScrap (0|1|2), now (optional: fixes "today" for inspection alerts)
// }
export function calculateCarTax(input, deps) {
  const moment = deps.moment;
  const vehicle = normalizeVehicle(input.carType, input.carFuel);
  const carType = vehicle.carType;
  const electric = isElectric(vehicle.carFuel);
  const scrapMode = input.carTaxScrap;
  const now = input.now ? moment(input.now) : moment();

  const state = {
    taxPrice: [0, 0, 0, 0, 0, 0],
    taxByYear: [0, 0, 0, 0],
    totalTaxPrice: [0, 0, 0, 0],
    popupMessages: ['', '', '', '', '', ''],
    lateAge: 0,
    carAge: 0,
    taxYearInit: null,
    taxYearEnded: null,
    netTaxPrice: 0,
    totalTax: 0,
    taxPenalty: 0,
    isActiveTaxYearEnd: !!input.isActiveTaxYearEnd,
    isActiveTaxYearInit: !!input.isActiveTaxYearInit
  };

  if (electric && !hasValidPositiveVehicleWeight(input.carWeight)) {
    state.popupMessages[0] =
      'รถไฟฟ้าต้องระบุน้ำหนักรถเป็นตัวเลขที่มากกว่า 0';
    return state;
  }

  state.lateAge = findAge(moment, input.taxFillingDate, input.billTaxoutDate);
  state.carAge = findAge(moment, input.taxFillingDate, input.registrationDate);

  state.taxPrice = calcBaseTaxPrices(
    { carType, carFuel: vehicle.carFuel, carWeight: input.carWeight, engineSize: input.engineSize }
  );

  state.popupMessages[4] = inspectionMessage(input, carType, moment, now);

  if (carType === 'CT1' && !electric) {
    state.taxByYear = taxByYearForAge(state.carAge, state.taxPrice);
  } else {
    state.taxByYear = [state.taxPrice[0], state.taxPrice[0], state.taxPrice[0], state.taxPrice[0]];
  }

  function taxYearPlusOne(years) {
    state.taxYearInit = moment(input.billTaxoutDate)
      .add(years, 'years')
      .set(MIDNIGHT)
      .format(DATE_TIME_FORMAT);
  }

  // Back tax + late fees, for the years the car went without paying.
  function totalLateTax() {
    const t = state.taxByYear;
    let penalty = 0;
    let total = 0;
    if (state.lateAge > 36) {
      state.popupMessages[1] =
        'รถโดน ระงับ ขาดเกิน 3 ปี ค่าภาษีที่แสดง มีค่าปรับ 3 ปี + ค่าภาษีปีใหม่';
      penalty = t[0] * 0.01 * 36 + t[1] * 0.01 * 24 + t[2] * 0.01 * 12;
      total = t[0] + t[1] + t[2] + t[3];
      state.totalTaxPrice[0] = penalty + total;
      taxYearPlusOne(3);
    } else if (state.lateAge > 24) {
      const x = state.lateAge - 24;
      penalty = t[0] * 0.01 * (24 + x) + t[1] * 0.01 * (12 + x) + t[2] * 0.01 * x;
      total = t[0] + t[1] + t[2];
      state.totalTaxPrice[0] = penalty + total;
      taxYearPlusOne(3);
    } else if (state.lateAge > 12) {
      const x = state.lateAge - 12;
      penalty = t[1] * 0.01 * (12 + x) + t[2] * 0.01 * x;
      total = t[1] + t[2];
      state.totalTaxPrice[0] = penalty + total;
      taxYearPlusOne(2);
    } else if (state.lateAge > 0) {
      penalty = t[2] * 0.01 * state.lateAge;
      total = t[2];
      state.totalTaxPrice[0] = penalty + total;
      taxYearPlusOne(1);
    } else {
      total = t[3];
      state.totalTaxPrice[0] = t[3];
      taxYearPlusOne(1);
    }
    state.taxPenalty = roundMoney(penalty, scrapMode);
    state.totalTax = roundMoney(
      state.isActiveTaxYearEnd ? total + t[3] : total,
      scrapMode
    );
    state.totalTaxPrice[1] = parseInt(
      moment(state.taxYearInit, DATE_TIME_FORMAT).format('YYYY'), 10
    );
  }

  // Can the owner prepay the next tax year? Fills the year-end fields.
  function oneYearTax() {
    const taxYearInit = moment(state.taxYearInit, DATE_TIME_FORMAT).set(MIDNIGHT);
    const taxFillingDate = moment(input.taxFillingDate, DATE_TIME_FORMAT).set(MIDNIGHT);
    const taxoutDate = moment(input.taxoutDate, DATE_TIME_FORMAT).set(MIDNIGHT);
    const resetTaxYearEnded = function () {
      state.taxYearEnded = null;
      state.totalTaxPrice[2] = 0;
      state.totalTaxPrice[3] = 0;
      state.isActiveTaxYearInit = false;
      state.isActiveTaxYearEnd = false;
    };

    if (input.dialogYearChoose) {
      resetTaxYearEnded();
      return;
    }
    if (!taxYearInit.isValid() || !taxFillingDate.isValid() || !taxoutDate.isValid()) {
      state.popupMessages[0] = '';
      resetTaxYearEnded();
      return;
    }

    const askDay = taxoutDate.clone().subtract(3, 'months').format(DATE_TIME_FORMAT);
    const firstTaxPayDate = taxoutDate.clone().subtract(3, 'months');
    const firstNextYearPayDate = taxYearInit.clone().subtract(3, 'months');
    const canPayCurrentTax = !taxFillingDate.isBefore(firstTaxPayDate);
    const canPayNextTaxYear =
      !taxFillingDate.isBefore(firstNextYearPayDate) &&
      !taxFillingDate.isAfter(taxYearInit);

    if (!canPayCurrentTax) {
      state.popupMessages[0] =
        'ไม่สามารถต่อได้ เพราะต่อภาษีล่วงหน้าได้ 3 เดือน สามารถต่อได้หลังวันที่ ' +
        moment(askDay).add(543, 'year').format('DD/MM/YYYY');
      resetTaxYearEnded();
    } else if (!canPayNextTaxYear) {
      state.popupMessages[0] = '';
      resetTaxYearEnded();
    } else {
      state.totalTaxPrice[2] = state.taxByYear[3];
      state.taxYearEnded = moment(state.taxYearInit, DATE_TIME_FORMAT)
        .add(1, 'years')
        .format(DATE_TIME_FORMAT);
      state.totalTaxPrice[3] = parseInt(
        moment(state.taxYearEnded, DATE_TIME_FORMAT).format('YYYY'), 10
      );
      state.popupMessages[0] = 'รถคันนี้สามารถต่อได้ถึงปี ' + (state.totalTaxPrice[3] + 543);
    }
  }

  // Gas discount and corporate surcharge. Electric cars have no adjustment here:
  // their discount is the 80% rate applied in the base tax.
  function taxDiscountOrIncrease() {
    let price = state.netTaxPrice;
    let total = state.totalTax;
    if (input.extraFuel == 2) {
      state.popupMessages[3] = 'อย่าลืมใบแก๊สด้วย';
    } else if (input.extraFuel == 1) {
      state.popupMessages[3] = 'อย่าลืมใบแก๊สด้วย ลดภาษี 25%';
      state.netTaxPrice = price - price / 4;
      price = state.netTaxPrice;
      state.totalTax = total - total / 4;
      total = state.totalTax;
    } else if (input.extraFuel == 3) {
      state.popupMessages[3] = 'อย่าลืมใบแก๊สด้วย ลดภาษี 50%';
      state.netTaxPrice = price - price / 2;
      price = state.netTaxPrice;
      state.totalTax = total - total / 2;
      total = state.totalTax;
    } else {
      state.popupMessages[3] = '';
      state.netTaxPrice = price;
    }
    if (input.isCorporationCar == true && carType === 'CT1') {
      state.netTaxPrice = price * 2;
      state.totalTax = total * 2;
    }
  }

  const wasActiveTaxYearEnd = state.isActiveTaxYearEnd;
  totalLateTax();
  oneYearTax();
  if (wasActiveTaxYearEnd && !state.isActiveTaxYearEnd) {
    totalLateTax();
  }
  state.netTaxPrice = state.totalTaxPrice[0];
  if (state.isActiveTaxYearEnd) {
    state.netTaxPrice = state.totalTaxPrice[0] + state.totalTaxPrice[2];
  }
  taxDiscountOrIncrease();
  state.netTaxPrice = roundMoney(state.netTaxPrice, scrapMode);

  return state;
}
